import { winningPositions } from './winningPositions';

// ── Lógica de decisión del bot, compartida entre SoloBotPage.js y SoloBotPageArg.js ──
//
// Modelo de PUNTAJE (scoring), no una lista de prioridades fija: cada casilla candidata recibe
// un puntaje táctico (ganar / bloquear / fork / proteger la propia / desarrollo posicional), al
// que se le suma ruido según la dificultad. Se ordena por (puntaje + ruido), lo que da variedad
// natural (el bot no hace siempre lo mismo) y permite escalar la habilidad de verdad entre los 5
// niveles. Todo es steal-aware: respeta casillas blindadas (`shielded`) y el tope de jugadas
// (`stealCap`), y contempla que el rival puede GANAR robando la 3ra casilla (no solo llenando una
// vacía).

// Cantidad de líneas ganadoras que pasan por cada casilla (0=esquina..4=centro..8=esquina):
// centro=4, esquinas=3, bordes=2.
export const SQUARE_LINE_VALUE = [3, 2, 3, 2, 4, 2, 3, 2, 3];

const randomFrom = (arr) => arr[Math.floor(Math.random() * arr.length)];

// ── Helpers de bajo nivel ────────────────────────────────────────────────────────────────────

// ¿Puede `player` tomar la casilla `idx` este turno? Vacía siempre; en Modo Robo también si es
// del rival y no está blindada ni llegó al tope. Nunca las propias.
function canTake(board, idx, player, { isStealMode, stealCap }) {
    const cell = board[idx];
    if (cell.value === '') return true;
    if (!isStealMode) return false;
    if (cell.value === player) return false;
    if (cell.shielded) return false;
    if (stealCap !== 'unlimited' && (cell.claims || 0) >= stealCap) return false;
    return true;
}

// Clásica: solo completa la línea si la 3ra celda está VACÍA. Se conserva para simulaciones
// simples (createsWinningThreat) y compatibilidad.
export function findWinningMove(board, player) {
    for (const [a, b, c] of winningPositions) {
        if (board[a].value === player && board[a].value === board[b].value && board[c].value === '') return c;
        if (board[a].value === player && board[a].value === board[c].value && board[b].value === '') return b;
        if (board[b].value === player && board[b].value === board[c].value && board[a].value === '') return a;
    }
    return null;
}

// Modo Robo: la 3ra celda puede estar vacía O ser tomable (robable, no blindada, bajo el tope).
export function findWinningMoveSteal(board, player, stealCap) {
    const ctx = { isStealMode: true, stealCap };
    for (const [a, b, c] of winningPositions) {
        if (board[a].value === player && board[a].value === board[b].value && canTake(board, c, player, ctx)) return c;
        if (board[a].value === player && board[a].value === board[c].value && canTake(board, b, player, ctx)) return b;
        if (board[b].value === player && board[b].value === board[c].value && canTake(board, a, player, ctx)) return a;
    }
    return null;
}

// Compat: centro si está vacío, o robable en Modo Robo. Ya no se usa en el scoring (el valor
// posicional lo cubre SQUARE_LINE_VALUE) pero se mantiene exportado por si algo externo lo llama.
export function findCenterMove(board, { isStealMode, stealCap, botSymbol } = {}) {
    if (board[4].value === '') return 4;
    if (isStealMode && canTake(board, 4, botSymbol, { isStealMode, stealCap })) return 4;
    return null;
}

// Simula ocupar `square` con `player` y devuelve el tablero resultante (copia superficial).
function simulate(board, square, player) {
    const next = [...board];
    next[square] = { ...(board[square] || {}), value: player, claims: ((board[square] && board[square].claims) || 0) + 1 };
    return next;
}

// Cantidad de líneas donde `player` tiene 2 casillas y la 3ra es tomable por él (amenazas reales,
// steal-aware). Es la base de forks (>=2) y amenazas simples (==1 nueva).
export function countThreats(board, player, ctx) {
    let n = 0;
    for (const [a, b, c] of winningPositions) {
        const line = [a, b, c];
        const owned = line.filter(i => board[i].value === player).length;
        if (owned !== 2) continue;
        const third = line.find(i => board[i].value !== player);
        if (canTake(board, third, player, ctx)) n++;
    }
    return n;
}

// Compat clásico: ¿jugar `square` deja al menos una amenaza? (solo celdas vacías)
export function createsWinningThreat(square, testSquares, player) {
    const testBoard = simulate(testSquares, square, player);
    return findWinningMove(testBoard, player) !== null;
}

// Casillas donde el bot puede jugar: vacías + robables al rival (no blindadas, bajo el tope).
// Nunca incluye las propias — las candidatas defensivas propias se agregan aparte en el scoring.
export function getPlayableSquares(squares, botSymbol, { isStealMode, stealCap } = {}) {
    const ctx = { isStealMode, stealCap };
    return squares.reduce((acc, square, idx) => {
        if (square.value === '') { acc.push(idx); return acc; }
        if (isStealMode && canTake(squares, idx, botSymbol, ctx)) acc.push(idx);
        return acc;
    }, []);
}

// Casillas del rival donde GANARÍA si jugara ahí el próximo turno (steal-aware). Separadas en:
//  - takeable: el bot puede ocuparlas ahora para negar el win. Incluye la 3ra celda que falta
//    (vacía o robable) Y, como alternativa, las DOS celdas que el rival YA tiene en esa misma
//    línea si son robables — robarle cualquiera de las dos rompe la línea igual, y esto importa
//    en la práctica porque a veces no hay ningún jugador disponible para la 3ra celda específica
//    (combo raro/agotado) y esta es la única salida real para evitar el jaque.
//  - ownThreatened: son del BOT y el rival las robaría para ganar → el bot no puede "tomarlas",
//    la única defensa es hacerlas permanentes (reforzar hasta el tope o blindar).
function opponentWinningCells(board, opp, botSymbol, ctx) {
    const oppCtx = { isStealMode: ctx.isStealMode, stealCap: ctx.stealCap };
    const takeable = new Set();
    const ownThreatened = new Set();
    for (const [a, b, c] of winningPositions) {
        const line = [a, b, c];
        const owned = line.filter(i => board[i].value === opp).length;
        if (owned !== 2) continue;
        const third = line.find(i => board[i].value !== opp);
        if (!canTake(board, third, opp, oppCtx)) continue; // el rival no puede completar esa línea
        if (canTake(board, third, botSymbol, ctx)) takeable.add(third);
        else if (board[third].value === botSymbol && !board[third].shielded) ownThreatened.add(third);
        for (const i of [a, b, c]) {
            if (board[i].value === opp && canTake(board, i, botSymbol, ctx)) takeable.add(i);
        }
    }
    return { takeable, ownThreatened };
}

// ── Perfiles por dificultad ──────────────────────────────────────────────────────────────────
// awareness = probabilidad (por turno, por componente) de que el bot "vea" esa táctica.
// noise = magnitud del ruido aditivo al puntaje final (más ruido = más aleatorio/débil/variado).
// blunder = probabilidad de tirar la mejor jugada y quedarse con la 2da.
// stealBias = multiplicador sobre las penalizaciones de robo sin sentido (más alto = roba menos).
// stealPositional = cuánto valora robar una casilla de alto valor posicional (centro/esquina) aun
// sin una razón táctica inmediata — a mayor dificultad, MÁS dispuesto a robar con criterio (no
// menos): un jugador fuerte de verdad usa el robo como arma, no solo como reacción táctica.
const PROFILES = {
    'very-easy': { win: 0.25, block: 0.05, fork: 0,    forkDef: 0,    threat: 0.05, protect: 0.10, stealBias: 0.7, stealPositional: 0,   noise: 9000, blunder: 0.5 },
    'easy':      { win: 0.6,  block: 0.4,  fork: 0.1,  forkDef: 0.05, threat: 0.3,  protect: 0.25, stealBias: 1.4, stealPositional: 0,   noise: 5000, blunder: 0.25 },
    'medium':    { win: 1,    block: 0.72, fork: 0.4,  forkDef: 0.3,  threat: 0.7,  protect: 0.5,  stealBias: 0.9, stealPositional: 70,  noise: 1800, blunder: 0.1 },
    'hard':      { win: 1,    block: 0.92, fork: 0.8,  forkDef: 0.65, threat: 0.9,  protect: 0.8,  stealBias: 0.5, stealPositional: 200, noise: 600,  blunder: 0.03 },
    'very-hard': { win: 1,    block: 1,    fork: 1,    forkDef: 0.95, threat: 1,    protect: 1,    stealBias: 0.35, stealPositional: 280, noise: 120,  blunder: 0 },
};

// Pesos base de cada componente táctico (antes del gating por awareness).
const W = {
    WIN_NOW: 10000,
    BLOCK_WIN: 9000,
    // Por encima de FORK+BLOCK_FORK combinados (5000+4000=9000): armar un fork propio no sirve de
    // nada si el rival te gana antes en su próximo turno por no haber defendido esta casilla.
    PROTECT_OWN_WIN_CELL: 9200,
    FORK: 5000,
    BLOCK_FORK: 4000,
    THREAT: 800,          // × nº de amenazas nuevas
    PROTECT_DEV: 600,
    POSITIONAL: 25,       // × SQUARE_LINE_VALUE[i]
    STEAL_PENALTY: -900,
    STEAL_BACK_RISK: -650,
};

// Roll de "awareness": ¿el bot aplica esta táctica este turno?
const sees = (prob) => Math.random() < prob;

// Puntúa una casilla candidata `sq` para el bot. `defensive` marca las candidatas propias que
// solo existen para proteger (reforzar/tapar). Devuelve el puntaje total.
function scoreSquare(squares, sq, meta, ctx, profile) {
    const { botSymbol, oppSymbol } = ctx;
    const cell = squares[sq];
    const isSteal = ctx.isStealMode && cell.value === oppSymbol; // robar al rival
    const isOwn = cell.value === botSymbol;                       // reforzar propia
    let score = 0;

    // Posicional (siempre, con jitter chico para variar aperturas entre casillas equivalentes).
    score += SQUARE_LINE_VALUE[sq] * W.POSITIONAL + Math.random() * 15;

    // Defensa de casilla propia amenazada: si el rival GANA YA robándola, máxima urgencia (por
    // encima de armar un fork propio, que no sirve de nada si el rival gana antes). Si solo le
    // arma un fork al rival (necesita un 2do movimiento, no gana todavía), prioridad más baja,
    // pareja a bloquear un fork ajeno — no debe competir con una amenaza inmediata real.
    if (isOwn) {
        if (meta.ownThreatened.has(sq) && sees(profile.protect)) score += W.PROTECT_OWN_WIN_CELL;
        else if (meta.ownForkRisk.has(sq) && sees(profile.forkDef)) score += W.BLOCK_FORK;
        else if (sees(profile.protect)) score += W.PROTECT_DEV * meta.devValue[sq];
        return score; // una casilla propia solo aporta defensa; no gana/roba
    }

    // Ganar ya (vacía o robando).
    const afterBot = simulate(squares, sq, botSymbol);
    const winsNow = lineCompletedBy(squares, sq, botSymbol);
    if (winsNow && sees(profile.win)) score += W.WIN_NOW;

    // Bloquear el win inmediato del rival ocupando su casilla ganadora.
    if (meta.blockCells.has(sq) && sees(profile.block)) score += W.BLOCK_WIN;

    // Fork propio (deja 2+ amenazas) / amenaza simple.
    const threatsAfter = countThreats(afterBot, botSymbol, ctx);
    if (threatsAfter >= 2 && sees(profile.fork)) score += W.FORK;
    else if (threatsAfter >= 1 && sees(profile.threat)) score += W.THREAT * threatsAfter;

    // Bloquear un fork del rival (si el rival jugara acá, se le arma doble amenaza).
    if (meta.oppForkCells.has(sq) && sees(profile.forkDef)) score += W.BLOCK_FORK;

    if (isSteal) {
        const isTactical = winsNow || meta.blockCells.has(sq) || threatsAfter >= 2;
        // Valor de robar una casilla de alto valor posicional (centro/esquina) aunque no haya
        // una razón táctica inmediata: a mayor dificultad, más peso (roba con criterio, no solo
        // quita porque puede) — esto es lo que hace que hard/very-hard sepan usar el robo como
        // arma en vez de jugar casi como si fuera el modo clásico.
        score += profile.stealPositional * (SQUARE_LINE_VALUE[sq] / 4);
        // Penalización por robar sin sentido / robo que el rival puede revertir enseguida.
        if (!isTactical) {
            score += W.STEAL_PENALTY * profile.stealBias;
            const claimsAfter = (cell.claims || 0) + 1;
            const stillStealable = ctx.stealCap === 'unlimited' || claimsAfter < ctx.stealCap;
            if (stillStealable) score += W.STEAL_BACK_RISK * profile.stealBias;
        }
    }

    return score;
}

// ¿Ocupar `sq` con `player` completa una línea (gana ya)? Chequeo directo sobre el tablero simulado.
function lineCompletedBy(board, sq, player) {
    const next = simulate(board, sq, player);
    for (const [a, b, c] of winningPositions) {
        if (next[a].value === player && next[b].value === player && next[c].value === player) return true;
    }
    return false;
}

// ── API pública usada por las páginas ────────────────────────────────────────────────────────

// Devuelve un ARRAY priorizado de casillas candidatas (no una sola): la página lo recorre y, si
// la mejor se queda sin jugadores disponibles, prueba la siguiente en vez de perder el turno.
export function pickBotSquareOrder(squares, difficulty, { isStealMode, stealCap, botSymbol, oppSymbol } = {}) {
    const ctx = { isStealMode, stealCap, botSymbol, oppSymbol };
    const profile = PROFILES[difficulty] || PROFILES.medium;

    const playable = getPlayableSquares(squares, botSymbol, { isStealMode, stealCap });
    if (playable.length === 0) return [];

    // Precálculo de metadata táctica compartida por todas las casillas.
    const { takeable: blockCells, ownThreatened } = isStealMode
        ? opponentWinningCells(squares, oppSymbol, botSymbol, ctx)
        : { takeable: new Set(blockCellsClassic(squares, oppSymbol)), ownThreatened: new Set() };

    // Casillas donde el rival armaría un fork si jugara ahí (para defenderlas). Incluye tanto las
    // que el bot podría ocupar (vacías/robables) como las PROPIAS del bot: si el rival puede
    // robarle una casilla propia y eso le arma un fork, también hay que defenderla (reforzar o
    // blindar) aunque todavía no sea un win inmediato. OJO: esto es una amenaza "blanda" (el rival
    // necesita un 2do movimiento para ganar), NO una amenaza inmediata — va en un set aparte
    // (`ownForkRisk`, no `ownThreatened`) para que nunca compita en prioridad con un jaque real de
    // 1 sola jugada (si no, "armar un fork propio" podría ganarle numéricamente a "evitar perder
    // ya", que es exactamente el tipo de blunder que esto tiene que evitar).
    const oppForkCells = new Set();
    for (const sq of playable) {
        const afterOpp = simulate(squares, sq, oppSymbol);
        if (countThreats(afterOpp, oppSymbol, ctx) >= 2) oppForkCells.add(sq);
    }
    const ownForkRisk = new Set();
    if (isStealMode) {
        squares.forEach((cell, idx) => {
            if (cell.value !== botSymbol || cell.shielded || ownThreatened.has(idx)) return;
            if (stealCap !== 'unlimited' && (cell.claims || 0) >= stealCap) return;
            const afterOppSteal = simulate(squares, idx, oppSymbol);
            if (countThreats(afterOppSteal, oppSymbol, ctx) >= 2) ownForkRisk.add(idx);
        });
    }

    // Valor defensivo de reforzar cada casilla propia (nº de líneas propias "vivas" que la cruzan).
    const devValue = {};
    const ownDefensiveCandidates = [];
    if (isStealMode) {
        squares.forEach((cell, idx) => {
            if (cell.value !== botSymbol || cell.shielded) return;
            if (stealCap !== 'unlimited' && (cell.claims || 0) >= stealCap) return; // ya tope, permanente
            const v = ownLineValue(squares, idx, botSymbol, oppSymbol);
            if (ownThreatened.has(idx) || ownForkRisk.has(idx) || v > 0) {
                devValue[idx] = Math.max(1, v);
                ownDefensiveCandidates.push(idx);
            }
        });
    }

    const meta = { blockCells, ownThreatened, ownForkRisk, oppForkCells, devValue };
    const candidates = [...playable, ...ownDefensiveCandidates];

    // Puntuar + ruido, ordenar desc.
    const scored = candidates.map(sq => ({
        sq,
        key: scoreSquare(squares, sq, meta, ctx, profile) + Math.random() * profile.noise,
    }));
    scored.sort((a, b) => b.key - a.key);

    // Blunder: a veces el bot tira la mejor y se queda con la 2da.
    if (scored.length > 1 && Math.random() < profile.blunder) {
        [scored[0], scored[1]] = [scored[1], scored[0]];
    }

    // Apertura: en dificultades altas, el bot prioriza el centro casi siempre — tanto si es la
    // 1ra jugada del juego (empieza el bot) como si es su propia 1ra jugada siendo 2do jugador.
    // Se aplica ANTES del bloqueo/victoria para que esas prioridades (más importantes) lo pisen.
    const totalPieces = squares.reduce((n, c) => n + (c.value !== '' ? 1 : 0), 0);
    if (totalPieces <= 1) {
        const centerProfile = CENTER_OPENING[difficulty] || CENTER_OPENING.medium;
        const chance = totalPieces === 0 ? centerProfile.first : centerProfile.second;
        const centerIdx = scored.findIndex(s => s.sq === 4);
        if (centerIdx > 0 && Math.random() < chance) {
            const [centerEntry] = scored.splice(centerIdx, 1);
            scored.unshift(centerEntry);
        }
    }

    // A partir de hard, si el rival tiene una amenaza de victoria (jaque), el bot SIEMPRE intenta
    // neutralizarla — no deja pasar el jaque por ruido/azar, y tampoco lo ignora persiguiendo un
    // fork propio (armar 2 amenazas no sirve de nada si el rival gana antes en su próximo turno).
    // Cubre las dos formas de "jaque": robarle al rival la casilla que le falta (blockCells) y
    // defender una casilla PROPIA que el rival robaría para ganar (ownThreatened, vía reforzar o
    // blindar). Si hay más de una amenaza a la vez (jaque mate real, imposible de tapar del todo
    // con una sola jugada), igual intenta neutralizar una de las dos en vez de ignorarlas.
    if (MUST_BLOCK_DIFFICULTIES.has(difficulty)) {
        const dangerIdx = scored.findIndex(s => meta.blockCells.has(s.sq) || meta.ownThreatened.has(s.sq));
        if (dangerIdx > 0) {
            const [dangerEntry] = scored.splice(dangerIdx, 1);
            scored.unshift(dangerEntry);
        }
    }

    // A partir de medium, si el bot puede ganar YA, tiene que ganar sí o sí — sin excepciones de
    // ruido ni de blunder. El puntaje (WIN_NOW=10000) ya lo prioriza casi siempre, pero el ruido
    // podría en teoría hacerlo perder ante otra jugada también muy puntuada (ej. un bloqueo); acá
    // se fuerza de forma determinística para que nunca desperdicie una victoria disponible. Va al
    // final porque ganar siempre pisa cualquier otra prioridad (incluido bloquear o el centro).
    if (MUST_WIN_DIFFICULTIES.has(difficulty)) {
        const ownSet = new Set(ownDefensiveCandidates);
        const winIdx = scored.findIndex(s => !ownSet.has(s.sq) && lineCompletedBy(squares, s.sq, botSymbol));
        if (winIdx > 0) {
            const [winEntry] = scored.splice(winIdx, 1);
            scored.unshift(winEntry);
        }
    }

    return scored.map(s => s.sq);
}

// A partir de qué dificultad el bot NUNCA puede desperdiciar una victoria disponible en su turno.
const MUST_WIN_DIFFICULTIES = new Set(['medium', 'hard', 'very-hard']);

// A partir de qué dificultad el bot SIEMPRE intenta tapar/robar una amenaza de victoria del rival.
const MUST_BLOCK_DIFFICULTIES = new Set(['hard', 'very-hard']);

// Probabilidad de ir al centro en la apertura: `first` = el bot mueve primero (1ra del juego),
// `second` = el bot mueve segundo (su propia 1ra jugada, con el centro todavía libre/robable).
const CENTER_OPENING = {
    'very-easy': { first: 0.2,  second: 0.15 },
    'easy':      { first: 0.5,  second: 0.35 },
    'medium':    { first: 0.85, second: 0.6 },
    'hard':      { first: 1,    second: 0.8 },
    'very-hard': { first: 1,    second: 0.9 },
};

// Bloqueo clásico (fuera de Modo Robo): casillas que completan la línea del rival en una celda vacía.
function blockCellsClassic(board, opp) {
    const cells = [];
    for (const [a, b, c] of winningPositions) {
        if (board[a].value === opp && board[a].value === board[b].value && board[c].value === '') cells.push(c);
        if (board[a].value === opp && board[a].value === board[c].value && board[b].value === '') cells.push(b);
        if (board[b].value === opp && board[b].value === board[c].value && board[a].value === '') cells.push(a);
    }
    return cells;
}

// Nº de líneas "vivas" para el bot que cruzan `idx` (el bot tiene 1+ y el rival 0): mide cuánto
// conviene asegurar esa casilla propia reforzándola.
function ownLineValue(board, idx, botSymbol, oppSymbol) {
    let v = 0;
    for (const [a, b, c] of winningPositions) {
        if (![a, b, c].includes(idx)) continue;
        const line = [a, b, c];
        if (line.some(i => board[i].value === oppSymbol)) continue; // línea muerta para el bot
        const owned = line.filter(i => board[i].value === botSymbol).length;
        if (owned >= 2) v += 2; // línea a punto de completarse
        else if (owned === 1) v += 1;
    }
    return v;
}

// Decide si el bot gasta su blindaje (único por partida) sobre la casilla `square` recién elegida.
// Steal-aware y escalado: blinda cuando la casilla queda bajo amenaza real de robo-para-ganar o
// asegura un fork, ponderado por la dificultad. Niveles bajos: raro/al azar.
export function shouldBotShield(square, boardAfterPlay, botSymbol, difficulty, shieldAlreadyUsed, ctx = {}) {
    if (shieldAlreadyUsed) return false;
    const oppSymbol = ctx.oppSymbol || (botSymbol === 'O' ? 'X' : 'O');
    const sctx = { isStealMode: true, stealCap: ctx.stealCap };

    // ¿El rival podría robar ESTA casilla el próximo turno para ganar? (defensa crítica)
    const { ownThreatened } = opponentWinningCells(boardAfterPlay, oppSymbol, botSymbol, { ...sctx, botSymbol, oppSymbol });
    const underWinThreat = ownThreatened.has(square);

    // ¿Esta casilla sostiene un fork del bot que el rival desarmaría robándola?
    const holdsFork = countThreats(boardAfterPlay, botSymbol, sctx) >= 2;

    const lineValue = SQUARE_LINE_VALUE[square];

    switch (difficulty) {
        case 'very-easy':
            return Math.random() < 0.10;
        case 'easy':
            if (underWinThreat) return Math.random() < 0.35;
            return Math.random() < 0.08;
        case 'medium':
            if (underWinThreat) return Math.random() < 0.7;
            if (holdsFork) return Math.random() < 0.5;
            return false;
        case 'hard':
            if (underWinThreat || holdsFork) return Math.random() < 0.9;
            return lineValue >= 4 && Math.random() < 0.3;
        case 'very-hard':
            // Guarda el blindaje para lo que de verdad importa; no lo malgasta de arranque.
            return underWinThreat || holdsFork;
        default:
            return underWinThreat;
    }
}

export { randomFrom };
